// Parsing, scaling and metric <-> imperial conversion of ingredient measures
// such as "1 1/2 cups flour", "200g butter" or "2 tbs oil".

const GLYPHS = { "½": 0.5, "¼": 0.25, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3, "⅛": 0.125 };
const GLYPH_BY_VALUE = [
  [0.125, "⅛"],
  [0.25, "¼"],
  [1 / 3, "⅓"],
  [0.5, "½"],
  [2 / 3, "⅔"],
  [0.75, "¾"],
];

// unit -> { kind, base (ml or g), system }
const UNITS = {
  ml: { kind: "vol", base: 1, system: "metric" },
  l: { kind: "vol", base: 1000, system: "metric" },
  litre: { kind: "vol", base: 1000, system: "metric" },
  litres: { kind: "vol", base: 1000, system: "metric" },
  liter: { kind: "vol", base: 1000, system: "metric" },
  liters: { kind: "vol", base: 1000, system: "metric" },
  tsp: { kind: "vol", base: 5, system: "both" },
  teaspoon: { kind: "vol", base: 5, system: "both" },
  teaspoons: { kind: "vol", base: 5, system: "both" },
  tbsp: { kind: "vol", base: 15, system: "both" },
  tbs: { kind: "vol", base: 15, system: "both" },
  tablespoon: { kind: "vol", base: 15, system: "both" },
  tablespoons: { kind: "vol", base: 15, system: "both" },
  cup: { kind: "vol", base: 240, system: "imperial" },
  cups: { kind: "vol", base: 240, system: "imperial" },
  pint: { kind: "vol", base: 473, system: "imperial" },
  pints: { kind: "vol", base: 473, system: "imperial" },
  floz: { kind: "vol", base: 29.57, system: "imperial" },
  g: { kind: "mass", base: 1, system: "metric" },
  gram: { kind: "mass", base: 1, system: "metric" },
  grams: { kind: "mass", base: 1, system: "metric" },
  kg: { kind: "mass", base: 1000, system: "metric" },
  oz: { kind: "mass", base: 28.35, system: "imperial" },
  ounce: { kind: "mass", base: 28.35, system: "imperial" },
  ounces: { kind: "mass", base: 28.35, system: "imperial" },
  lb: { kind: "mass", base: 453.6, system: "imperial" },
  lbs: { kind: "mass", base: 453.6, system: "imperial" },
  pound: { kind: "mass", base: 453.6, system: "imperial" },
  pounds: { kind: "mass", base: 453.6, system: "imperial" },
};

const AMOUNT_RE = /^\s*(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?\s*[½¼¾⅓⅔⅛]?|[½¼¾⅓⅔⅛]|\.\d+)\s*(.*)$/;

const toNumber = (token) => {
  const t = token.trim();
  const mixed = t.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const frac = t.match(/^(\d+)\/(\d+)$/);
  if (frac) return Number(frac[1]) / Number(frac[2]);
  const glyph = t.match(/^(\d+(?:\.\d+)?)?\s*([½¼¾⅓⅔⅛])$/);
  if (glyph) return Number(glyph[1] || 0) + GLYPHS[glyph[2]];
  return Number(t);
};

/** "1 1/2 cups flour" -> { value: 1.5, rest: "cups flour" }; null if no leading amount. */
export function parseAmount(str) {
  if (!str) return null;
  const m = String(str).match(AMOUNT_RE);
  if (!m) return null;
  const value = toNumber(m[1]);
  return Number.isFinite(value) ? { value, rest: m[2] } : null;
}

const splitUnit = (rest) => {
  const fl = rest.match(/^fl\.?\s*oz\.?\b\s*(.*)$/i);
  if (fl) return { unit: "floz", name: fl[1] };
  const m = rest.match(/^([a-zA-Z]+)\.?(?=\s|$|[,;)])\s*(.*)$/);
  if (m && UNITS[m[1].toLowerCase()]) return { unit: m[1].toLowerCase(), name: m[2] };
  return { unit: null, name: rest };
};

/** 1.5 -> "1½", 0.333 -> "⅓", 2.4 -> "2.4" */
export function formatQty(n) {
  if (!Number.isFinite(n)) return "";
  const whole = Math.floor(n + 1e-9);
  const frac = n - whole;
  if (frac < 0.04) return String(whole || (n > 0 ? "0" : "0"));
  if (frac > 0.96) return String(whole + 1);
  const hit = GLYPH_BY_VALUE.find(([v]) => Math.abs(frac - v) < 0.04);
  if (hit) return `${whole || ""}${hit[1]}`;
  return String(Math.round(n * 100) / 100);
};

const roundMetric = (n) => {
  if (n >= 100) return String(Math.round(n / 5) * 5);
  if (n >= 10) return String(Math.round(n));
  return String(Math.round(n * 10) / 10);
};

const convert = (value, unit, system) => {
  const u = UNITS[unit];
  if (system === "metric") {
    if (u.system === "metric" || u.system === "both") return null;
    const base = value * u.base;
    if (u.kind === "mass") return base >= 1000 ? `${roundMetric(base / 1000)} kg` : `${roundMetric(base)} g`;
    return base >= 1000 ? `${roundMetric(base / 1000)} l` : `${roundMetric(base)} ml`;
  }
  if (system === "imperial") {
    if (u.system === "imperial" || u.system === "both") return null;
    const base = value * u.base;
    if (u.kind === "mass") {
      return base >= 453.6
        ? `${formatQty(Math.round((base / 453.6) * 4) / 4)} lb`
        : `${formatQty(Math.round((base / 28.35) * 4) / 4 || 0.25)} oz`;
    }
    if (base < 15) return `${formatQty(Math.round((base / 5) * 4) / 4 || 0.25)} tsp`;
    if (base < 60) return `${formatQty(Math.round((base / 15) * 2) / 2 || 0.5)} tbsp`;
    return `${formatQty(Math.round((base / 240) * 4) / 4)} cup${base / 240 > 1.1 ? "s" : ""}`;
  }
  return null;
};

/**
 * Scale by `ratio` (servings) and optionally convert to "metric" / "imperial".
 * Strings without a parsable amount are returned unchanged.
 */
export function formatMeasure(original, { ratio = 1, system = "original" } = {}) {
  if (!original) return original;
  if (ratio === 1 && system === "original") return original;
  const parsed = parseAmount(original);
  if (!parsed) return original;

  const value = parsed.value * ratio;
  const { unit, name } = splitUnit(parsed.rest);

  if (unit && system !== "original") {
    const converted = convert(value, unit, system);
    if (converted) return `${converted} ${name}`.trim();
  }
  if (ratio === 1) return original;
  return `${formatQty(value)} ${parsed.rest}`.trim();
}
